import React, { useState, useEffect } from 'react';
import { X, Share2, Link as LinkIcon, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default captions per share source — light, on-brand, edit-friendly
const DEFAULT_CAPTIONS = {
  id: 'Just IDed this banger with BEATSEEK 🎯',
  discovery: "Diggin' this one 🔥",
  similar: 'AI matched me with this one 🤖',
  calendar_event: "Don't sleep on this one 🗓️🔥",
  gig: 'Gig alert — who wants it? 🎧',
  jam: "We're jamming live — pull up! 🎧🔊",
};

const SOURCE_BADGES = {
  id: { label: 'IDed on BEATSEEK', color: '#00ff88', bg: 'rgba(0,255,136,0.12)', border: 'rgba(0,255,136,0.4)' },
  discovery: { label: 'Discovered on BEATSEEK', color: '#22d3ee', bg: 'rgba(6,182,212,0.12)', border: 'rgba(6,182,212,0.4)' },
  similar: { label: 'AI Match on BEATSEEK', color: '#e879f9', bg: 'rgba(192,38,211,0.12)', border: 'rgba(192,38,211,0.4)' },
  calendar_event: { label: '🗓️ Event on BEATSEEK', color: '#a78bfa', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.4)' },
  gig: { label: '💼 Gig on BEATSEEK', color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.4)' },
  jam: { label: '🎧 Live Jam Session', color: '#00ff88', bg: 'rgba(0,255,136,0.12)', border: 'rgba(0,255,136,0.4)' },
};

const EVENT_ICONS = { calendar_event: '🗓️', gig: '💼', jam: '🎧' };

/**
 * ShareToWallModal — reusable share-to-Wall sheet.
 *
 * Track mode props:
 *   source: 'id' | 'discovery' | 'similar'
 *   track: { title, artist, artwork, bpm, key, genre, spotify_uri }
 * Event mode props:
 *   source: 'calendar_event' | 'gig' | 'jam'
 *   eventItem: { title, subtitle, date_line, location, genre, image, link, session_id, extra }
 * Common:
 *   isOpen, onClose, currentUser: { id, username, profile_picture }, onPosted?
 */
const ShareToWallModal = ({ isOpen, onClose, source = 'discovery', track, eventItem, currentUser, onPosted }) => {
  const [caption, setCaption] = useState(DEFAULT_CAPTIONS[source] || '');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [cardBlob, setCardBlob] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [attachCard, setAttachCard] = useState(true);

  // Reset state every time we open with (potentially) different content
  useEffect(() => {
    if (isOpen) {
      setCaption(DEFAULT_CAPTIONS[source] || '');
      setError(null);
      setSuccess(false);
      setCopied(false);
      setCardUrl(null);
      setCardBlob(null);
      setAttachCard(true);
    }
  }, [isOpen, source, track?.title, track?.artist, eventItem?.title]);

  if (!isOpen || (!track && !eventItem)) return null;

  const isEvent = !!eventItem;
  const badge = SOURCE_BADGES[source] || SOURCE_BADGES.discovery;

  // Track-mode payload
  const artwork = track?.artwork || track?.artwork_url || '';
  const bpm = track?.bpm || track?.bpm_key?.tempo;
  const keyName = track?.key || track?.bpm_key?.key_name;
  const sharedTrack = track ? {
    source,
    title: track.title || '',
    artist: track.artist || '',
    artwork,
    bpm: bpm ? Math.round(Number(bpm)) : null,
    key: keyName || null,
    genre: track.genre || null,
    version_type: track.versionType || track.version_type || null,
    remixer: track.remixer || null,
    label: track.label || null,
    subgenres: track.subgenres || [],
    registered_by: track.community?.registered_by || null,
    spotify_uri: track.spotify_uri || track.uri || null,
    external_url: track.external_url || track.url || null,
  } : null;

  // Event-mode payload
  const sharedEvent = eventItem ? {
    source,
    title: eventItem.title || '',
    subtitle: eventItem.subtitle || null,
    date_line: eventItem.date_line || null,
    location: eventItem.location || null,
    genre: eventItem.genre || null,
    image: eventItem.image || null,
    link: eventItem.link || null,
    session_id: eventItem.session_id || null,
    extra: eventItem.extra || null,
  } : null;

  const handlePost = async () => {
    if (posting) return;
    if (!currentUser?.id) {
      setError('You must be logged in to share to the Wall.');
      return;
    }
    setPosting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('content_type', isEvent ? 'shared_event' : 'shared_track');
      formData.append('caption', caption || '');
      formData.append('user_id', currentUser.id);
      formData.append('username', currentUser.username || currentUser.name || '');
      formData.append('avatar', currentUser.profile_picture || '');
      formData.append('highlight', 'none');
      if (isEvent) formData.append('shared_event', JSON.stringify(sharedEvent));
      else formData.append('shared_track', JSON.stringify(sharedTrack));
      if (!isEvent && cardBlob && attachCard) {
        formData.append('media', new File([cardBlob], 'beatseek-track-card.png', { type: 'image/png' }));
      }

      const res = await fetch(`${API}/api/wall/posts`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `HTTP ${res.status}`);
      }
      const post = await res.json();
      setSuccess(true);
      if (onPosted) onPosted(post);
      setTimeout(() => {
        setSuccess(false);
        onClose && onClose();
      }, 1100);
    } catch (e) {
      setError(`Failed to post: ${e.message || 'Unknown error'}`);
    } finally {
      setPosting(false);
    }
  };

  const handleCopyLink = async () => {
    const link = isEvent
      ? (sharedEvent.link || `${window.location.origin}/`)
      : (sharedTrack.spotify_uri || sharedTrack.external_url || `${window.location.origin}/`);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      setError('Could not copy link');
    }
  };

  const shareToInstagram = async () => {
    if (!cardBlob) return;
    const fileName = `beatseek-${(sharedTrack.title || 'track').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-card.png`;
    const file = new File([cardBlob], fileName, { type: 'image/png' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${sharedTrack.artist} – ${sharedTrack.title}`, text: caption || 'ID\'d with BEATSEEK' });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    const a = document.createElement('a');
    a.href = cardUrl; a.download = fileName; a.click();
    setError('Card saved to your device — open Instagram and post it from your camera roll (1080×1350, Story & feed ready).');
  };

  const generateCard = async () => {
    if (cardLoading) return;
    setCardLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/track-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: sharedTrack.title,
          artist: sharedTrack.artist,
          artwork: sharedTrack.artwork || null,
          bpm: sharedTrack.bpm,
          key: sharedTrack.key,
          genre: sharedTrack.genre,
          version_type: sharedTrack.version_type,
          remixer: sharedTrack.remixer,
          label: sharedTrack.label,
          subgenres: sharedTrack.subgenres,
          registered_by: sharedTrack.registered_by,
          source,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      setCardBlob(blob);
      setAttachCard(true);
      setCardUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError('Image card generation failed — please try again');
    }
    setCardLoading(false);
  };

  const previewImage = isEvent ? sharedEvent.image : artwork;
  const previewTitle = isEvent ? sharedEvent.title : (sharedTrack.title || 'Untitled');
  const previewSub = isEvent ? (sharedEvent.subtitle || '') : (sharedTrack.artist || 'Unknown');
  const chips = isEvent
    ? [sharedEvent.date_line, sharedEvent.location && `📍 ${sharedEvent.location}`, sharedEvent.genre && `🎵 ${sharedEvent.genre}`, sharedEvent.extra].filter(Boolean)
    : [];

  return (
    <div
      data-testid="share-to-wall-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100005,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '420px',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(6,182,212,0.12)',
          padding: '18px',
          position: 'relative',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Share2 size={16} style={{ color: '#22d3ee' }} />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#fff' }}>Share to The Wall</h3>
          </div>
          <button
            data-testid="share-to-wall-close"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa',
            }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Preview card */}
        <div
          style={{
            display: 'flex', gap: '12px', padding: '10px',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${badge.border}`,
            borderRadius: '10px', marginBottom: '12px',
          }}
        >
          <div
            style={{
              width: 64, height: 64, borderRadius: '8px', flexShrink: 0,
              background: previewImage
                ? `url(${previewImage}) center/cover no-repeat`
                : 'linear-gradient(135deg, #1a1a2e, #0d0d14)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#22d3ee', fontSize: isEvent ? '26px' : '10px', fontWeight: 700,
            }}
          >
            {!previewImage && (isEvent ? (EVENT_ICONS[source] || '🗓️') : 'BEATSEEK')}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {previewTitle || 'Untitled'}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {previewSub}
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {isEvent && chips.map((c, i) => (
                <span key={i} style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', fontWeight: 600 }}>
                  {c}
                </span>
              ))}
              {!isEvent && sharedTrack.bpm && (
                <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(255,170,0,0.08)', color: '#fbbf24', fontWeight: 600 }}>
                  🥁 {sharedTrack.bpm}
                </span>
              )}
              {!isEvent && sharedTrack.key && (
                <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(192,38,211,0.10)', color: '#e879f9', fontWeight: 600 }}>
                  🎹 {sharedTrack.key}
                </span>
              )}
              {!isEvent && sharedTrack.genre && (
                <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', fontWeight: 600 }}>
                  {sharedTrack.genre}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Source badge */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
          <span
            style={{
              fontSize: '10px', padding: '4px 10px', borderRadius: '10px',
              background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
              fontWeight: 700, letterSpacing: '0.02em',
            }}
          >
            {badge.label}
          </span>
        </div>

        {/* Caption editor */}
        <label style={{ display: 'block', fontSize: '10px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Your caption
        </label>
        <textarea
          data-testid="share-to-wall-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={isEvent ? 'Say something about this event…' : 'Say something about this track…'}
          maxLength={280}
          rows={3}
          style={{
            width: '100%', padding: '10px', borderRadius: '8px',
            background: 'rgba(255,255,255,0.04)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.08)',
            fontSize: '13px', resize: 'vertical', outline: 'none',
            fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
        <div style={{ fontSize: '9px', color: '#555', textAlign: 'right', marginTop: '2px' }}>
          {caption.length}/280
        </div>

        {/* Error / Success */}
        {error && (
          <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px', padding: '6px 8px', background: 'rgba(239,68,68,0.08)', borderRadius: '6px' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ fontSize: '11px', color: '#00ff88', marginTop: '6px', padding: '6px 8px', background: 'rgba(0,255,136,0.08)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Check size={12} /> Posted to The Wall!
          </div>
        )}

        {/* Card-attach indicator */}
        {!isEvent && cardUrl && (
          <div
            data-testid="card-attach-indicator"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
              fontSize: '11px', marginTop: '8px', padding: '6px 10px', borderRadius: '8px',
              background: attachCard ? 'rgba(192,38,211,0.10)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${attachCard ? 'rgba(192,38,211,0.4)' : 'rgba(255,255,255,0.08)'}`,
              color: attachCard ? '#e879f9' : '#888',
            }}
          >
            <span>{attachCard ? '🖼️ Image card will be attached to your post' : 'Image card won\'t be attached'}</span>
            <button
              data-testid="card-attach-toggle"
              onClick={() => setAttachCard(a => !a)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700,
                color: attachCard ? '#f0abfc' : '#22d3ee', fontSize: '11px', padding: 0, whiteSpace: 'nowrap',
              }}
            >
              {attachCard ? '✕ don\'t attach' : '+ attach it'}
            </button>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
          <button
            data-testid="share-to-wall-copy-link"
            onClick={handleCopyLink}
            disabled={posting}
            style={{
              flex: 1, padding: '10px', borderRadius: '8px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#ccc', fontSize: '12px', fontWeight: 700,
              cursor: posting ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              transition: 'all 0.2s',
            }}
          >
            {copied ? <><Check size={13} /> Copied</> : <><LinkIcon size={13} /> Copy Link</>}
          </button>
          <button
            data-testid="share-to-wall-post"
            onClick={handlePost}
            disabled={posting || success}
            style={{
              flex: 1.4, padding: '10px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #06b6d4 0%, #c026d3 100%)',
              boxShadow: '0 0 18px rgba(6,182,212,0.32)',
              border: 'none', color: '#fff', fontSize: '12px', fontWeight: 800,
              cursor: posting || success ? 'wait' : 'pointer',
              opacity: posting ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
          >
            {posting ? 'Posting…' : success ? 'Posted ✓' : 'Post to Wall'}
          </button>
        </div>

        {/* Shareable image card — track mode only */}
        {!isEvent && (
          <div style={{ marginTop: '10px' }}>
            {cardUrl ? (
              <div>
                <img
                  src={cardUrl}
                  alt="Shareable track card"
                  data-testid="track-card-preview"
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '10px', border: '1px solid rgba(6,182,212,0.3)', background: '#07070b' }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <a
                    href={cardUrl}
                    download={`beatseek-${(sharedTrack.title || 'track').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-card.png`}
                    data-testid="track-card-download"
                    style={{
                      flex: 1, padding: '9px', borderRadius: '8px', textAlign: 'center',
                      background: 'rgba(0,255,136,0.12)', color: '#00ff88',
                      border: '1px solid rgba(0,255,136,0.4)', fontSize: '12px', fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    ⬇️ Download Card
                  </a>
                  <button
                    type="button"
                    onClick={shareToInstagram}
                    data-testid="track-card-instagram"
                    style={{
                      flex: 1, padding: '9px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(131,58,180,0.25), rgba(253,29,29,0.22), rgba(252,176,69,0.22))',
                      color: '#fff', border: '1px solid rgba(253,29,29,0.45)', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    📸 Instagram
                  </button>
                  <button
                    onClick={generateCard}
                    disabled={cardLoading}
                    style={{
                      flex: 1, padding: '9px', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.04)', color: '#ccc',
                      border: '1px solid rgba(255,255,255,0.08)', fontSize: '12px', fontWeight: 700,
                      cursor: cardLoading ? 'wait' : 'pointer',
                    }}
                  >
                    {cardLoading ? 'Rendering…' : '↻ Regenerate'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="track-card-generate"
                onClick={generateCard}
                disabled={cardLoading}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  background: 'rgba(192,38,211,0.10)',
                  border: '1px solid rgba(192,38,211,0.4)',
                  color: '#e879f9', fontSize: '12px', fontWeight: 700,
                  cursor: cardLoading ? 'wait' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {cardLoading ? '🎨 Rendering your card…' : '🖼️ Image Card — branded 1080×1350 PNG for IG / socials'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareToWallModal;
